<template>
  <SubmitForm v-if="showSubmitForm" :x="x" :y="y" @submit="onSubmit" @cancel="onCancel" />

  <div class="flex flex-1 justify-center items-center h-full">
    <div id="map" class="min-w-[1093px] min-h-[747px] relative">
      <SvgMap
        alt="World Map"
        width="1093"
        height="747"
        class="absolute inset-0"
        @click="onMapClick"
      />
      <MapPin v-if="showPin" :x="x" :y="y" :scale="zoomScale" />
      <MapPin
        v-for="pin in filteredPins"
        :class="{
          hidden: createMode
        }"
        :key="pin._id"
        :id="pin._id"
        :x="pin.x"
        :y="pin.y"
        :scale="zoomScale"
        :selected="selectedPin?._id === pin._id"
        @click="onPinClick(pin)"
      />
      <MapPin
        v-for="pin in organizationPins"
        :class="{
          hidden: createMode,
          '!text-blue-500': true
        }"
        :key="pin._id"
        :id="pin._id"
        :x="pin.x"
        :y="pin.y"
        :scale="zoomScale"
        :selected="selectedPin?._id === pin._id"
        @click="onPinClick(pin)"
      />
      <ErasmusPlusLogo class="absolute -bottom-24 right-1/3 translate-x-1/2" />
      <FundedByEuLogo class="absolute -bottom-24 left-1/3 -translate-x-1/2" />
    </div>
  </div>

  <div
    v-if="createMode"
    class="alert bg-primary text-white absolute z-20 bottom-0 sm:bottom-8 w-full max-w-xl left-1/2 -translate-x-1/2 flex"
  >
    <InformationCircleIcon class="fill-info shrink-0 w-8 h-8" />
    <div class="w-full flex items-center justify-between">
      {{ showPin ? $t('locationCorrect') : $t('selectLocation') }}
      <div>
        <button
          v-if="showPin"
          class="btn btn-circle btn-sm btn-success"
          @click="(createMode = false), (showSubmitForm = true)"
        >
          <CheckIcon class="w-4 h-4" />
        </button>
        <button
          v-if="showPin"
          class="btn btn-circle btn-sm btn-error ml-2"
          @click="showPin = false"
        >
          <XMarkIcon class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>

  <PreviewModal
    v-if="showPreview && selectedPin"
    @close="(showPreview = false), setSelectedPin(undefined)"
    @see-more="(showPreview = false), (showPinModal = true)"
  />

  <PinModal
    v-if="showPinModal && selectedPin"
    @close="(showPinModal = false), setSelectedPin(undefined)"
    @update="fetchPins()"
  />

  <NavigationButton
    class="bottom-40"
    :tooltip="$t('randomActivity')"
    @click="() => selectRandomPin()"
  >
    <RandomIcon />
  </NavigationButton>

  <NavigationButton class="bottom-24" :tooltip="$t('centerMap')" @click="centerMap">
    <GlobeAltIcon class="w-6 h-6 transition-transform" />
  </NavigationButton>

  <NavigationButton
    class="bottom-8"
    :toggled="createMode"
    :tooltip="createMode ? $t('cancel') : $t('createActivity')"
    @click="toggleCreateMode"
  >
    <PlusIcon class="w-6 h-6 transition-transform" :class="createMode ? 'rotate-45' : 'rotate-0'" />
  </NavigationButton>

  <div class="fixed left-1 bottom-1 text-black/25 text-xs">
    x: {{ lastClickX }}, y: {{ lastClickY }}
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

import ErasmusPlusLogo from '@/assets/erasmus.svg'
import FundedByEuLogo from '@/assets/funded-by-eu.svg'
import SvgMap from '@/assets/new-map.svg'
import { usePinsStore } from '@/stores/pins'
import { CheckIcon, XMarkIcon } from '@heroicons/vue/20/solid'
import { GlobeAltIcon, InformationCircleIcon, PlusIcon } from '@heroicons/vue/24/solid'
import Panzoom from '@panzoom/panzoom'
import { storeToRefs } from 'pinia'

import { organizationPins } from '@/utils/data'

import type { Pin } from '@/interfaces'
import type { PanzoomObject } from '@panzoom/panzoom'

import RandomIcon from '@/components/custom-icons/RandomIcon.vue'
import MapPin from '@/components/MapPin.vue'
import NavigationButton from '@/components/NavigationButton.vue'
import PinModal from '@/components/PinModal.vue'
import PreviewModal from '@/components/PreviewModal.vue'
import SubmitForm from '@/components/SubmitForm.vue'

const props = defineProps<{
  focusedPin?: Pin
}>()

const emit = defineEmits<{
  (e: 'pin-focused'): void
}>()

const panzoom = ref<PanzoomObject>()

const showSubmitForm = ref(false)
const createMode = ref(false)
const showPin = ref(false)
const x = ref(0)
const y = ref(0)
const zoomScale = ref(0)

const lastClickX = ref(0)
const lastClickY = ref(0)

const showPreview = ref(false)
const showPinModal = ref(false)

const pinsStore = usePinsStore()
const { setSelectedPin, fetchPins } = pinsStore
const { selectedPin, filteredPins } = storeToRefs(pinsStore)

onMounted(async () => {
  const elem = document.getElementById('map')

  if (elem) {
    panzoom.value = Panzoom(elem, {
      maxScale: 1000,
      startScale: 0.75,
      minScale: 0.75,
      canvas: true,
      step: 1,
      roundPixels: true
    })
    elem.parentElement?.addEventListener('wheel', panzoom.value.zoomWithWheel)

    elem.addEventListener('panzoomzoom', (event: any) => {
      zoomScale.value = event.detail.scale
    })
  }

  if (props.focusedPin) {
    setSelectedPin(props.focusedPin)
    showPinModal.value = true

    emit('pin-focused')
  }
})

watch(createMode, (value) => {
  if (panzoom.value) {
    panzoom.value.setOptions({
      cursor: value ? 'crosshair' : 'move',
      disablePan: value,
      disableZoom: value
    })
  }
})

const toggleCreateMode = () => {
  createMode.value = !createMode.value
  showPin.value = false
}

const onMapClick = (event: any) => {
  const { offsetX, offsetY } = event

  lastClickX.value = offsetX
  lastClickY.value = offsetY

  if (createMode.value) {
    x.value = offsetX
    y.value = offsetY
    showPin.value = true
  }
}

const centerMap = () => {
  if (panzoom.value) {
    panzoom.value.pan(0, -150)
    panzoom.value.zoom(0.75)
  }
}

const onSubmit = () => {
  showPin.value = false
  showSubmitForm.value = false
  fetchPins()
}

const onCancel = () => {
  showPin.value = false
  showSubmitForm.value = false
}

const onPinClick = (pin: Pin) => {
  setSelectedPin(pin)
  showPreview.value = true
  showPinModal.value = false
}

const selectRandomPin = () => {
  if (!filteredPins.value) {
    return
  }

  const randomPin = filteredPins.value[Math.floor(Math.random() * filteredPins.value.length)]

  setSelectedPin(randomPin)
  showPreview.value = true
  showPinModal.value = false
}
</script>
